import { MediaAssetProviders } from '@/features/ai-video-creator/stores/productMediaSlice';
import { MediaAssetType } from '@/types/media';
import { TemplateName, CategoryName } from '@/features/studio-video-templates/types/template';
import { StepperGenerationType } from '@/features/video-generation-stepper/types';
import { LibraryViews } from '@/features/my-creations-library/contants';

export enum AvatarsPageViews {
  default = 'default',
  personal = 'personal',
}

export enum PageTitle {
  Home = 'Home',
  Settings = 'Settings',
  Plans = 'Plans',
  Library = 'Library',
  Avatars = 'Influencers',
  CreateAvatar = 'Create Influencer',
  CreateProject = 'Create',
  AITools = 'AI Tools',
  AIPhoto = 'AI Photo',
  AnimateProduct = 'Animate Product',
  ReplaceBackground = 'Replace Background',
  TextToVideo = 'Text to Video',
  TextToImage = 'Generate image',
  ChatToImage = 'Chat to image',
  TryOn = 'Try On',
  Publish = 'Publish',
  Integrations = 'Integrations',
  WebsiteTemplates = 'Website Templates',
  Assets = 'My Assets',
  NewVideo = 'Templates',
  Editor = 'Editor',
  MakeWithChat = 'Make With Chat',
  Labs = 'Labs (Beta)',
}

enum PageRoute {
  Home = '/home',
  Settings = '/settings',
  Plans = '/settings/plans',
  Library = '/library',
  Avatars = '/avatars',
  CreateAvatar = '/create/tools/ai-avatar',
  AITools = '/create/tools',
  AIPhoto = '/create/tools/ai-photo',
  AnimateProduct = '/create/tools/animate-product',
  ReplaceBackground = '/create/tools/replace-background',
  TextToVideo = '/create/tools/text-to-video',
  TextToImage = '/create/tools/text-to-image',
  ChatToImage = '/create/tools/chat-to-image',
  TryOn = '/create/tools/try-on',
  Publish = '/publish',
  Integrations = '/integrations',
  Assets = '/sources/assets',
  WebsiteTemplates = '/templates/website',
  NewVideo = '/new-video',
  CreateProject = '/new-video/stepper',
  Editor = '/editor',
  MakeWithChat = '/make-with-chat',
  Labs = '/labs',
}

export const pageRouteTitleMap: Record<PageRoute, PageTitle> = {
  [PageRoute.Home]: PageTitle.Home,
  [PageRoute.Settings]: PageTitle.Settings,
  [PageRoute.Plans]: PageTitle.Plans,
  [PageRoute.Library]: PageTitle.Library,
  [PageRoute.Avatars]: PageTitle.Avatars,
  [PageRoute.CreateAvatar]: PageTitle.CreateAvatar,
  [PageRoute.CreateProject]: PageTitle.CreateProject,
  [PageRoute.AITools]: PageTitle.AITools,
  [PageRoute.AIPhoto]: PageTitle.AIPhoto,
  [PageRoute.AnimateProduct]: PageTitle.AnimateProduct,
  [PageRoute.ReplaceBackground]: PageTitle.ReplaceBackground,
  [PageRoute.TextToVideo]: PageTitle.TextToVideo,
  [PageRoute.TextToImage]: PageTitle.TextToImage,
  [PageRoute.TryOn]: PageTitle.TryOn,
  [PageRoute.Publish]: PageTitle.Publish,
  [PageRoute.Integrations]: PageTitle.Integrations,
  [PageRoute.WebsiteTemplates]: PageTitle.WebsiteTemplates,
  [PageRoute.Assets]: PageTitle.Assets,
  [PageRoute.NewVideo]: PageTitle.NewVideo,
  [PageRoute.Editor]: PageTitle.Editor,
  [PageRoute.ChatToImage]: PageTitle.ChatToImage,
  [PageRoute.MakeWithChat]: PageTitle.MakeWithChat,
  [PageRoute.Labs]: PageTitle.Labs,
};

export enum AITools {
  aiPhoto = 'aiPhoto',
  animateProduct = 'animateProduct',
  replaceBackground = 'replaceBackground',
  textToVideo = 'textToVideo',
  textToImage = 'textToImage',
  chatToImage = 'chatToImage',
  tryOn = 'tryOn',
  aiModel = 'aiModel',
}

const AIToolRoutes: Record<AITools, string> = {
  [AITools.aiPhoto]: 'ai-photo',
  [AITools.animateProduct]: 'animate-product',
  [AITools.replaceBackground]: 'replace-background',
  [AITools.textToVideo]: 'text-to-video',
  [AITools.textToImage]: 'ai-photo',
  [AITools.chatToImage]: 'chat-to-image',
  [AITools.tryOn]: 'try-on',
  [AITools.aiModel]: 'ai-model',
};

const StudioRoutes = {
  createProject: (
    params: {
      id?: string;
      assetId?: string;
      assetSrc?: string;
      assetType?: MediaAssetType;
      assetProvider?: MediaAssetProviders;
      template?: TemplateName;
      category?: CategoryName;
      avatar?: string;
      defaultAvatar?: string;
      stepperGenerationType?: StepperGenerationType;
    } = {}
  ) => {
    const baseUrl = PageRoute.CreateProject;
    const searchParams = new URLSearchParams(params);
    const queryString = searchParams.toString();
    return queryString ? `${baseUrl}?${queryString}` : baseUrl;
  },
  library: (params: { creationTemplateFilter?: TemplateName; view?: LibraryViews } = {}) => {
    const baseUrl = PageRoute.Library;
    const searchParams = new URLSearchParams(params);
    const queryString = searchParams.toString();
    return queryString ? `${baseUrl}?${queryString}` : baseUrl;
  },
  aiTool: (tool: AITools) => {
    return `${PageRoute.AITools}/${AIToolRoutes[tool]}`;
  },
  aiTools: () => PageRoute.AITools,
  animateImage: (params: {
    assetId?: string;
    assetSrc: string;
    assetType: MediaAssetType;
    assetProvider?: MediaAssetProviders;
  }) => {
    const animatePath = StudioRoutes.aiTool(AITools.animateProduct);
    const searchParams = new URLSearchParams(params);
    const queryString = searchParams.toString();
    return `${animatePath}?${queryString}`;
  },
  publish: () => PageRoute.Publish,
  integrations: () => PageRoute.Integrations,
  plans: () => PageRoute.Plans,
  home: () => PageRoute.Home,
  websiteTemplates: () => PageRoute.WebsiteTemplates,
  createAvatar: () => PageRoute.CreateAvatar,
  avatars: (params: { view?: AvatarsPageViews } = {}) => {
    const baseUrl = PageRoute.Avatars;
    const searchParams = new URLSearchParams(params);
    const queryString = searchParams.toString();
    return queryString ? `${baseUrl}?${queryString}` : baseUrl;
  },
  assets: ({ providerKey }: { providerKey?: string }) => `${PageRoute.Assets}?providerKey=${providerKey}`,
  newVideo: () => PageRoute.NewVideo,
  settings: () => PageRoute.Settings,
  editor: () => PageRoute.Editor,
  makeWithChat: () => PageRoute.MakeWithChat,
  labs: () => PageRoute.Labs,
  productTryOn: (params: {
    assetId: string;
    assetSrc: string;
    assetType: MediaAssetType;
    assetProvider: MediaAssetProviders;
  }) => {
    const baseUrl = PageRoute.TryOn;
    const searchParams = new URLSearchParams(params);
    const queryString = searchParams.toString();
    return queryString ? `${baseUrl}?${queryString}` : baseUrl;
  },
} as const;

export default StudioRoutes;
